import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, MessageSquareText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

type Template = {
  id: string;
  title: string;
  channel: 'email' | 'sms' | 'both';
  subject: string | null;
  body: string;
  sort_order: number;
  is_active: boolean;
};

export function MessageTemplatesCard() {
  const { mode, dealershipId, role } = useAuth();
  const [items, setItems] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<{ title: string; channel: 'email' | 'sms' | 'both'; subject: string; body: string }>({
    title: '', channel: 'both', subject: '', body: '',
  });

  const isManager = role === 'manager' || role === 'admin';

  const load = async () => {
    if (!dealershipId) return;
    setLoading(true);
    const { data } = await supabase
      .from('message_templates')
      .select('*')
      .eq('dealership_id', dealershipId)
      .order('sort_order', { ascending: true });
    setItems((data ?? []) as Template[]);
    setLoading(false);
  };

  useEffect(() => {
    if (mode === 'real') load();
  }, [mode, dealershipId]);

  if (mode !== 'real' || !isManager) return null;

  const handleAdd = async () => {
    if (!draft.title.trim() || !draft.body.trim() || !dealershipId) {
      toast.error('Title and message are required');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('message_templates').insert({
      dealership_id: dealershipId,
      title: draft.title.trim(),
      channel: draft.channel,
      subject: draft.channel === 'sms' ? null : (draft.subject.trim() || null),
      body: draft.body.trim(),
      sort_order: items.length,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    setDraft({ title: '', channel: 'both', subject: '', body: '' });
    toast.success('Template added');
    load();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('message_templates').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    setItems(items.filter(t => t.id !== id));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareText className="h-4 w-4 text-primary" /> Message templates
        </CardTitle>
        <CardDescription>
          Reusable email and SMS snippets your team can drop into customer messages. Use{' '}
          <code className="text-xs">{'{{first_name}}'}</code> or{' '}
          <code className="text-xs">{'{{customer_name}}'}</code> for personalization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No templates yet. Add your first below.</p>
        ) : (
          <div className="space-y-2">
            {items.map(t => (
              <div key={t.id} className="flex items-start gap-3 p-3 rounded-md border border-border bg-muted/30">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{t.title}</p>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {t.channel}
                    </span>
                  </div>
                  {t.subject && <p className="text-xs text-muted-foreground mt-1 truncate">Subject: {t.subject}</p>}
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} aria-label="Delete template">
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 pt-4 border-t border-border">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">New template</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-title">Title</Label>
              <Input id="tpl-title" value={draft.title} placeholder="e.g. Still interested?"
                onChange={e => setDraft({ ...draft, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select value={draft.channel} onValueChange={(v) => setDraft({ ...draft, channel: v as 'email' | 'sms' | 'both' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="email">Email only</SelectItem>
                  <SelectItem value="sms">SMS only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {draft.channel !== 'sms' && (
            <div className="space-y-1.5">
              <Label htmlFor="tpl-subject">Email subject (optional)</Label>
              <Input id="tpl-subject" value={draft.subject} placeholder="Following up on your {{first_name}}…"
                onChange={e => setDraft({ ...draft, subject: e.target.value })} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="tpl-body">Message</Label>
            <Textarea id="tpl-body" value={draft.body} rows={4}
              placeholder="Hi {{first_name}}, just checking in…"
              onChange={e => setDraft({ ...draft, body: e.target.value })} />
          </div>
          <Button onClick={handleAdd} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add template</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}